import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, FileText, Eye, Check, X, Clock, Info, ListChecks, MessageSquarePlus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import api, { formatApiError } from "@/lib/api";

export default function AdminDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/applications/${id}`).then(({ data }) => setA(data));
  useEffect(() => { load(); }, [id]);

  const setStatus = async (status) => {
    if ((status === "Declined" || status === "More Information Required") && !reason.trim()) {
      return toast.error("Please add a reason / note for the applicant.");
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/applications/${id}/status`, { status, reason, note: reason });
      setA(data);
      setReason("");
      toast.success(`Marked ${status}. Applicant notified.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/admin/applications/${id}/note`, { note });
      setA((prev) => ({ ...prev, review_notes: data.review_notes }));
      setNote("");
      toast.success("Note added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const viewDoc = async (docId) => {
    try {
      const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open document"); }
  };

  if (!a) return (<div className="min-h-screen bg-[#FAF6F0]"><Navbar /><div className="p-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div></div>);

  const docs = (a.documents || []).filter((d) => !d.is_deleted);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF6F0]">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#8B1E2B]" data-testid="back-to-queue"><ArrowLeft className="h-4 w-4" /> {t("admin_queue")}</button>

        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-serif text-3xl text-[#8B1E2B]" data-testid="admin-detail-title">
            {a.groom?.full_name || "—"} <span className="text-slate-300">&</span> {a.bride?.full_name || "—"}
          </h1>
          <StatusBadge status={a.status} lang={lang} />
        </div>
        <p className="text-sm text-slate-500 mt-1">{a.applicant_account?.name} · {a.applicant_account?.email} · {a.applicant_account?.phone}</p>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <PersonCard title={t("step_groom")} p={a.groom} />
            <PersonCard title={t("step_bride")} p={a.bride} />

            <Card icon={<ListChecks className="h-5 w-5" />} title={t("step_consent")}>
              <div className="grid sm:grid-cols-2 gap-4">
                {["groom_side", "bride_side"].map((s) => (
                  <div key={s} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                    <div className="font-semibold text-slate-800">{s === "groom_side" ? "Groom's family" : "Bride's family"}</div>
                    <Line l={t("parent_guardian")} v={a.parents_consent?.[s]?.parent_name} />
                    <Line l="Relation" v={a.parents_consent?.[s]?.relation} />
                    <Line l={t("phone")} v={a.parents_consent?.[s]?.phone} />
                    {a.parents_consent?.[s]?.exception_reason && <div className="mt-1 text-xs text-amber-700">Exception: {a.parents_consent[s].exception_reason}</div>}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Consents — Groom: {a.consents?.groom_ack ? "✓" : "—"} · Bride: {a.consents?.bride_ack ? "✓" : "—"} · Privacy: {a.consents?.privacy_ack ? "✓" : "—"}
              </div>
            </Card>

            <Card icon={<FileText className="h-5 w-5" />} title={t("documents")}>
              {docs.length === 0 ? <p className="text-sm text-slate-400">No documents uploaded.</p> : (
                <ul className="space-y-2">
                  {docs.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                      <span className="min-w-0"><span className="text-xs uppercase tracking-wide text-amber-700 mr-2">{d.category}</span><span className="text-slate-700 truncate">{d.original_filename}</span></span>
                      <Button size="sm" variant="outline" onClick={() => viewDoc(d.id)} data-testid={`admin-doc-view-${d.id}`} className="border-amber-500/40 shrink-0"><Eye className="h-4 w-4 mr-1.5" /> View</Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {/* Actions */}
          <div className="space-y-6">
            <Card icon={<Check className="h-5 w-5" />} title="Decision">
              <label className="text-sm text-slate-600">{t("reason")}</label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" placeholder="Shared with the applicant on More-Info / Decline" data-testid="admin-reason-input" />
              <div className="mt-4 grid grid-cols-2 gap-2">
                <ActBtn onClick={() => setStatus("Under Review")} busy={busy} testid="admin-under-review-button" cls="bg-indigo-600 hover:bg-indigo-700" icon={<Clock className="h-4 w-4" />}>{t("under_review")}</ActBtn>
                <ActBtn onClick={() => setStatus("More Information Required")} busy={busy} testid="admin-more-info-button" cls="bg-amber-600 hover:bg-amber-700" icon={<Info className="h-4 w-4" />}>{t("more_info")}</ActBtn>
                <ActBtn onClick={() => setStatus("Verified")} busy={busy} testid="admin-verify-button" cls="bg-emerald-700 hover:bg-emerald-800" icon={<Check className="h-4 w-4" />}>{t("approve_verify")}</ActBtn>
                <ActBtn onClick={() => setStatus("Waitlisted")} busy={busy} testid="admin-waitlist-button" cls="bg-orange-600 hover:bg-orange-700" icon={<Clock className="h-4 w-4" />}>{t("waitlist")}</ActBtn>
                <ActBtn onClick={() => setStatus("Declined")} busy={busy} testid="admin-decline-button" cls="bg-red-700 hover:bg-red-800 col-span-2" icon={<X className="h-4 w-4" />}>{t("decline")}</ActBtn>
              </div>
            </Card>

            <Card icon={<MessageSquarePlus className="h-5 w-5" />} title={t("add_note")}>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder="Internal / applicant-visible review note" data-testid="admin-note-input" />
              <Button onClick={addNote} className="mt-3 w-full bg-[#8B1E2B] hover:bg-[#6f1622] text-white" data-testid="admin-add-note-button">{t("add_note")}</Button>
              {a.review_notes?.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {a.review_notes.map((n) => (
                    <li key={n.id} className="rounded-lg bg-amber-50/60 border border-amber-200 p-2.5 text-xs text-slate-700">{n.note}<div className="text-slate-400 mt-1">{n.by} · {new Date(n.at).toLocaleString()}</div></li>
                  ))}
                </ul>
              )}
            </Card>

            <Card icon={<Clock className="h-5 w-5" />} title={t("timeline")}>
              <ol className="space-y-3">
                {[...(a.timeline || [])].reverse().map((tl, i) => (
                  <li key={i} className="text-xs"><span className="font-medium text-slate-800">{tl.status}</span><div className="text-slate-400">{new Date(tl.at).toLocaleString()}</div></li>
                ))}
              </ol>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-amber-500/20 p-5 shadow-sm">
      <h3 className="font-serif text-lg text-slate-900 flex items-center gap-2 mb-4"><span className="text-[#8B1E2B]">{icon}</span> {title}</h3>
      {children}
    </div>
  );
}
function Line({ l, v }) {
  return <div className="flex justify-between gap-3 py-0.5"><span className="text-slate-500">{l}</span><span className="text-slate-900 text-right truncate">{v || "—"}</span></div>;
}
function PersonCard({ title, p }) {
  const { t } = useLang();
  return (
    <Card icon={<FileText className="h-5 w-5" />} title={title}>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <Line l={t("full_name")} v={p?.full_name} />
        <Line l={t("dob")} v={p?.dob} />
        <Line l={t("age_on_event")} v={p?.age_on_event != null ? `${p.age_on_event} yrs` : ""} />
        <Line l={t("phone")} v={p?.phone} />
        <Line l={t("email")} v={p?.email} />
        <Line l="Occupation" v={p?.occupation} />
      </dl>
      <div className="mt-2 text-sm"><Line l={t("address")} v={p?.address} /></div>
    </Card>
  );
}
function ActBtn({ onClick, busy, testid, cls, icon, children }) {
  return (
    <Button onClick={onClick} disabled={busy} data-testid={testid} className={`text-white text-xs ${cls}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}<span className="ml-1.5">{children}</span>
    </Button>
  );
}
